import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView } from 'angular-calendar';
import { MonthViewDay } from 'calendar-utils';
import { differenceInDays, endOfDay, endOfMonth, endOfWeek, isBefore, isSameDay, isSunday, startOfDay, startOfMonth, startOfWeek} from 'date-fns';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
@Component({
    selector: 'sgt-calendar',
    templateUrl: 'calendar.component.html',
    styleUrls: ['./calendar.component.less']
})
export class CalendarComponent implements OnInit, OnDestroy {
    viewDate: Date;
    @Input() startDate: Date = new Date();
    @Output() startDateChange = new EventEmitter<Date>();
    @Input() endDate: Date = new Date();
    @Output() endDateChange = new EventEmitter<Date>();
    @Output() datesChange = new EventEmitter<void>();

    @Input() events: CalendarEvent[];
    @Input() limitEventsShowing = 5;
    @Output() eventsChange = new EventEmitter<CalendarEvent[]>();
    @Output() eventClicked = new EventEmitter<CalendarEvent>();
    @Output() eventUpdated = new EventEmitter<CalendarEvent>();

    @Input() locale = 'pt-BR';
    @Input() eventTemplate: TemplateRef<any>;

    @ViewChild('calendarBody') calendarBody: ElementRef;
    view: CalendarView = CalendarView.Month;

    CalendarView = CalendarView;
    activeDayIsOpen = true;

    trackByEventId = (index: number, event: CalendarEvent) => event.id ? event.id : event;

    isBreakPointSmall: boolean;
    private destroy$ = new Subject<void>();

    constructor(private breakpointObserver: BreakpointObserver,
                private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (!this.startDate || !this.endDate) {
            this.onViewDateChange(new Date());
        } else {
            // this.viewDate = this.startDate;
            this.onViewDateChange(this.startDate);
        }

        this.createBreakPoints();
    }

    /** Registrando evento de alteração de break points */
    private createBreakPoints() {
        const CALENDAR_RESPONSIVE = {
            small: {
                breakpoint: '(max-width: 576px)',
                daysInWeek: 2,
            }
        };

        this.breakpointObserver.observe(Object.values(CALENDAR_RESPONSIVE).map(({ breakpoint }) => breakpoint))
            .pipe(
                takeUntil(this.destroy$)
            )
            .subscribe((state: BreakpointState) => {
                if (this.breakpointObserver.isMatched(CALENDAR_RESPONSIVE.small.breakpoint)) {
                    this.isBreakPointSmall = true;
                } else {
                    this.isBreakPointSmall = false;
                }

                this.cd.markForCheck();
            });
    }

    /** Ao clicar em algum dia do calendar */
    dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
        (jQuery(this.calendarBody.nativeElement).find('.dropdown-toggle') as any).dropdown('hide');
    }

    /** Ao atualizar um evento */
    private updateEvent(event: CalendarEvent, newStart: Date, newEnd?: Date, allDay?: boolean): CalendarEvent {
        return {
            ...event,
            start: newStart,
            end: newEnd,
            allDay,
        };
    }

    /** Ao atualizar as datas de um evento */
    eventTimesChanged({ event, newStart, newEnd, allDay}: CalendarEventTimesChangedEvent): void {
        this.events = this.events.map((iEvent) => {
            if (iEvent === event) {
                return this.updateEvent(event, newStart, newEnd, allDay);
            }
            return iEvent;
        });
        this.eventsChange.emit(this.events);

        event = this.updateEvent(event, newStart, newEnd, allDay);
        this.eventUpdated.emit(event);
    }

    /** Ao deletar um evento */
    deleteEvent(eventToDelete: CalendarEvent) {
        this.events = this.events.filter((event) => event !== eventToDelete);
    }

    /** Altera a view do calendar */
    setView(view: CalendarView) {
        this.view = view;
        this.onViewDateChange(this.viewDate);
    }

    /** Ao alterar a data do calendar */
    onViewDateChange(newViewDate: Date) {
        this.viewDate = newViewDate;

        if (this.view === CalendarView.Day) { // Day
            this.startDate = startOfDay(newViewDate);
            this.endDate = endOfDay(newViewDate);
        } else if (this.view === CalendarView.Week) { // Week
            this.startDate = startOfWeek(newViewDate);
            this.endDate = endOfWeek(newViewDate);
        } else { // Month
            this.startDate = startOfMonth(newViewDate);
            this.endDate = endOfMonth(newViewDate);
        }

        this.startDateChange.emit(this.startDate);
        this.endDateChange.emit(this.endDate);
        this.datesChange.emit();
    }

    /** Obtem o css class do event container */
    getEventContainerClass(event: CalendarEvent, monthDay: MonthViewDay): string {
        const day = monthDay.date;
        const isSundayDay = isSunday(day);
        const isStartDay = isSameDay(event.start, day);
        if (!isSundayDay && !isStartDay) {
           return 'cal-event-container-stub';
        }

        return '';
    }

    /** Obtem os styles class do event container */
    getEventContainerStyle(event: CalendarEvent, monthDay: MonthViewDay): Object {
        const day = monthDay.date;
        const isSundayDay = isSunday(day);
        const isStartDay = isSameDay(event.start, day);
        if (!isSundayDay && !isStartDay) {
            return {
                width: '100%',
                visibility: 'visible'
            };
        }

        let startDate: Date;
        if (isSameDay(event.start, day)) {
            startDate = event.start;
        } else {
            startDate = day;
        }

        const endDayOfWeek = endOfWeek(day);
        let endDate: Date;
        if (isBefore(event.end, endDayOfWeek)) {
            endDate = event.end;
        } else {
            endDate = endDayOfWeek;
        }

        let daysBetween = differenceInDays(
            new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 0, 0, 0, 0),
            new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0, 0)
        );
        daysBetween += 1;

        // Acrescentando as bordas do cellDay no tamanho através da multiplicação da quantidade de dias por 2 bordas (esquerda e direita)
        const bordersWidth = daysBetween * 2;

        return {
            width: `calc(${daysBetween}00% + ${bordersWidth}px)`,
            'z-index': 20
        }
    }

    /** Exibe mais eventos que estão ocultos */
    showMoreEvents(day: MonthViewDay, clickEvent: MouseEvent) {
        if (!day.meta) {
            day.meta = {};
        }

        day.meta.limitEventsShowing = -1;
        day.meta.iShowingMoreEvents = true;
        clickEvent.stopPropagation();
    }

    /** Oculta os mais eventos */
    showLessEvents(day: MonthViewDay, clickEvent: MouseEvent) {
        if (!day.meta) {
            day.meta = {};
        }

        day.meta.limitEventsShowing = this.limitEventsShowing;
        day.meta.iShowingMoreEvents = false;
        clickEvent.stopPropagation();
    }

    /** Ao clicar no dropdown das ações */
    dropdownEventActionsClick(dropdownEventActions: HTMLDivElement, mouseEvent: MouseEvent) {
        // Abrindo/Fechando o Dropdown
        (jQuery(dropdownEventActions).find('.dropdown-toggle') as any).dropdown('toggle');
        // Paramos o evento do click para não abrir a atividade
        mouseEvent.stopPropagation();
    }

    /** Ao criar em alguma ação */
    actionClick(event: CalendarEvent, action: CalendarEventAction, mouseEvent: MouseEvent, dropdownEventActions: HTMLDivElement) {
        action.onClick({event, sourceEvent: mouseEvent});

        (jQuery(dropdownEventActions).find('.dropdown-toggle') as any).dropdown('hide');

        // Paramos o evento do click para não abrir a atividade
        mouseEvent.stopPropagation();
    }

    ngOnDestroy() {
        this.destroy$.next();
    }
}

